import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { DealCheck, DealCheckFormData } from '@/types';
import { eurosToCents } from '@/lib/utils';

interface DealCheckState {
  dealCheck: DealCheck | null;
  isLoading: boolean;
  error: string | null;

  fetchDealCheck: (propertyId: string) => Promise<void>;
  saveDealCheck: (propertyId: string, data: DealCheckFormData) => Promise<void>;
  clearError: () => void;
}

export const useDealCheckStore = create<DealCheckState>((set) => ({
  dealCheck: null,
  isLoading: false,
  error: null,

  fetchDealCheck: async (propertyId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('deal_checks')
        .select('*')
        .eq('property_id', propertyId)
        .maybeSingle();

      if (error) throw error;
      set({ dealCheck: data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden des Deal-Checks';
      set({ error: message, isLoading: false });
    }
  },

  saveDealCheck: async (propertyId, data) => {
    set({ isLoading: true, error: null });
    try {
      const payload = {
        property_id: propertyId,
        estimated_exit_value: eurosToCents(data.estimated_exit_value ?? null),
        exit_value_reasoning: data.exit_value_reasoning || null,
        exit_confidence: data.exit_confidence,
        asking_price: eurosToCents(data.asking_price ?? null),
        cost_transfer_tax: eurosToCents(data.cost_transfer_tax ?? null),
        cost_notary: eurosToCents(data.cost_notary ?? null),
        cost_land_registry: eurosToCents(data.cost_land_registry ?? null),
        cost_broker: eurosToCents(data.cost_broker ?? null),
        cost_other: eurosToCents(data.cost_other ?? null),
        estimated_renovation: eurosToCents(data.estimated_renovation ?? null),
        pros: data.pros || null,
        cons: data.cons || null,
        open_questions: data.open_questions || null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('deal_checks')
        .upsert(payload, { onConflict: 'property_id' });

      if (error) throw error;

      // Refetch
      const { data: updatedData } = await supabase
        .from('deal_checks')
        .select('*')
        .eq('property_id', propertyId)
        .single();

      set({ dealCheck: updatedData, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Speichern';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
